import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ticketAPI } from '@/lib/api';
import { UserPlus, Send, AlertCircle, Calendar, Clock, Phone } from 'lucide-react';

const SecondLineAssignment = ({ ticket, onAssignmentComplete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    second_line_staff_name: '',
    second_line_staff_email: '',
    second_line_staff_contact: '',
    assignment_reason: '',
    target_resolution_date: '',
    target_resolution_time: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.second_line_staff_name.trim()) {
      toast.error('Please enter subordinate name');
      return;
    }
    
    if (!formData.second_line_staff_email.trim()) {
      toast.error('Please enter subordinate email');
      return;
    }
    
    if (!formData.second_line_staff_contact.trim()) {
      toast.error('Please enter subordinate contact number');
      return;
    }
    
    if (!formData.assignment_reason.trim()) {
      toast.error('Please provide a message description for the subordinate');
      return;
    }
    
    if (!formData.target_resolution_date || !formData.target_resolution_time) {
      toast.error('Please set target resolution date and time');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.second_line_staff_email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Combine date and time into ISO string
    const targetDateTime = new Date(`${formData.target_resolution_date}T${formData.target_resolution_time}`).toISOString();
    
    setLoading(true);
    try {
      await ticketAPI.assignSecondLine(ticket.ticket_id, {
        second_line_staff_name: formData.second_line_staff_name,
        second_line_staff_email: formData.second_line_staff_email,
        second_line_staff_contact: formData.second_line_staff_contact,
        assignment_reason: formData.assignment_reason,
        target_resolution_datetime: targetDateTime
      });
      toast.success('Ticket reassigned successfully! Email notification sent to subordinate.');
      setFormData({
        second_line_staff_name: '',
        second_line_staff_email: '',
        second_line_staff_contact: '',
        assignment_reason: '',
        target_resolution_date: '',
        target_resolution_time: ''
      });
      setIsExpanded(false);
      if (onAssignmentComplete) {
        setTimeout(() => {
          onAssignmentComplete();
        }, 3000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign ticket');
    } finally {
      setLoading(false);
    }
  };

  // Don't show if ticket is already resolved
  if (ticket.status === 'Resolved') {
    return null;
  }

  // Show if already reassigned
  if (ticket.second_line_staff_email) {
    return (
      <Card className="border-pink-200 bg-pink-50/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-pink-700 flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Reassigned to Subordinate
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div>
            <span className="text-slate-500">Reassigned To:</span>{' '}
            <span className="font-medium">{ticket.second_line_staff_name}</span>
          </div>
          <div>
            <span className="text-slate-500">Email:</span>{' '}
            <span className="text-pink-600">{ticket.second_line_staff_email}</span>
          </div>
          {ticket.second_line_staff_contact && (
            <div>
              <span className="text-slate-500">Contact:</span>{' '}
              <span className="font-mono">{ticket.second_line_staff_contact}</span>
            </div>
          )}
          <div>
            <span className="text-slate-500">Reason:</span>{' '}
            <span className="text-slate-700">{ticket.second_line_assignment_reason}</span>
          </div>
          {ticket.target_resolution_datetime && (
            <div>
              <span className="text-slate-500">Target Resolution:</span>{' '}
              <span className="text-pink-700 font-medium">
                {new Date(ticket.target_resolution_datetime).toLocaleString('en-IN', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Reassign Ticket
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs"
            data-testid="toggle-reassign-btn"
          >
            {isExpanded ? 'Cancel' : 'Reassign'}
          </Button>
        </div>
        <CardDescription className="text-xs">
          On leave or vacation? Reassign this ticket to your subordinate.
        </CardDescription>
      </CardHeader>
      
      {isExpanded && (
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Email notification will be sent to the subordinate with CC to you (ticket resolver) and Level 1 admin.</span>
            </div>
            
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="subordinate_name" className="text-sm">Subordinate Name <span className="text-red-500">*</span></Label>
                <Input
                  id="subordinate_name"
                  placeholder="Enter subordinate name"
                  value={formData.second_line_staff_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, second_line_staff_name: e.target.value }))}
                  data-testid="subordinate-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subordinate_email" className="text-sm">Subordinate Email <span className="text-red-500">*</span></Label>
                <Input
                  id="subordinate_email"
                  type="email"
                  placeholder="staff@mitgurukul.com"
                  value={formData.second_line_staff_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, second_line_staff_email: e.target.value }))}
                  data-testid="subordinate-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subordinate_contact" className="text-sm flex items-center gap-1">
                <Phone className="w-3 h-3" />
                Subordinate Contact Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="subordinate_contact"
                type="tel"
                inputMode="tel"
                placeholder="+91 9876543210"
                value={formData.second_line_staff_contact}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/[^\d+]/g, '');
                  setFormData(prev => ({ ...prev, second_line_staff_contact: cleaned }));
                }}
                className="font-mono"
                autoComplete="tel"
                data-testid="subordinate-contact-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reassign_message" className="text-sm">Message Description <span className="text-red-500">*</span></Label>
              <Textarea
                id="reassign_message"
                placeholder="E.g., I am on leave from 5th to 10th April. Please handle this ticket during my absence. The issue requires checking the plumbing in Block 2."
                value={formData.assignment_reason}
                onChange={(e) => setFormData(prev => ({ ...prev, assignment_reason: e.target.value }))}
                rows={3}
                data-testid="reassign-message-input"
              />
            </div>
            
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="target_date" className="text-sm flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Target Resolution Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="target_date"
                  type="date"
                  value={formData.target_resolution_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_resolution_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="reassign-target-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="target_time" className="text-sm flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Target Resolution Time <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="target_time"
                  type="time"
                  value={formData.target_resolution_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_resolution_time: e.target.value }))}
                  data-testid="reassign-target-time"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-pink-600 hover:bg-pink-700"
              data-testid="reassign-submit-btn"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Reassigning...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Reassign Ticket
                </>
              )}
            </Button>
          </form>
        </CardContent>
      )}
    </Card>
  );
};

export default SecondLineAssignment;
